'use client';

import { BreadcrumbProps } from '@commerce-product-webcomponents/sdk';
import { SearchTrackingTypes, SearchViewTransformerProps } from '@commerce-search-webcomponents/sdk';
import { SearchResult } from '@commerce-search-webcomponents/ui-composite';
import {
  sendUtagView,
  transformerRegistry,
  useNextgenRouter,
  useTealiumDataLayer,
  useTranslation,
} from '@nextgen-web-framework/all';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../../feature/store';
import styles from './SearchResultsPage.module.scss';
import { EventNameEnum } from 'app/components/Tealium/types/propTypes';
import Layout from 'app/components/Layout/Layout';
import { BasePageProps } from 'types/common';

const SearchResultsPage = (props: BasePageProps) => {
  const translate = useTranslation();
  const dataLayer = useTealiumDataLayer();
  const router = useNextgenRouter();
  const text = router.query?.get('text') ?? '';

  const header = props.data?.layout?.header;
  const footer = props.data?.layout?.footer;
  const crumbsData: BreadcrumbProps = {
    breadcrumbData: [],
  };
  crumbsData.breadcrumbData.push(
    {
      name: translate('web.bottomTabNavigation.home'),
      link: '/',
    },
    {
      name: (text || '').toString(),
      link: (text || '').toString(),
    },
  );

  const [sendViewEvent, setSendViewEvent] = useState(true);
  const {
    fulltextSearchResults,
    originallySearchedText,
    searchTrackingType,
    searchCurrentTab,
    articlesSearchResults,
    offersSearchResults,
    contentSearchLoaders,
    errors: { fulltextSearchError },
  } = useAppSelector((state) => state.commerceSearch);

  const totalCount = () => {
    switch (searchCurrentTab) {
      case 'products':
        return fulltextSearchResults?.total;
      case 'articles':
        return articlesSearchResults?.total_size;
      case 'offers':
        return offersSearchResults?.total_size;
      default:
        return (
          (fulltextSearchResults?.total ?? 0) +
          (articlesSearchResults?.total_size ?? 0) +
          (offersSearchResults?.total_size ?? 0)
        );
    }
  };

  useEffect(() => {
    const hasResults = Object.keys(fulltextSearchResults).length > 0;
    const isReadyToSend =
      searchTrackingType &&
      sendViewEvent &&
      hasResults &&
      !contentSearchLoaders.isArticlesLoading &&
      !contentSearchLoaders.isOffersLoading &&
      !fulltextSearchError;
    if (isReadyToSend) {
      const searchResultData: SearchViewTransformerProps = {
        searchResults: fulltextSearchResults,
        search_type: searchTrackingType,
        text: searchTrackingType === SearchTrackingTypes.CORRECTED ? originallySearchedText : text,
        articlesSearchResults,
        offersSearchResults,
      };
      const transformedData =
        transformerRegistry[EventNameEnum.onsite_search]?.<SearchViewTransformerProps>(searchResultData);
      setSendViewEvent(false);
      sendUtagView(EventNameEnum.onsite_search, dataLayer, transformedData);
    }
  }, [
    fulltextSearchResults,
    sendViewEvent,
    searchTrackingType,
    fulltextSearchError,
    articlesSearchResults,
    offersSearchResults,
  ]);

  useEffect(() => {
    setSendViewEvent(true);
  }, [text]);

  return (
    <Layout
      hasTopMobileHeader={true}
      header={header}
      footer={footer}
      crumbsData={crumbsData}
      headerProps={{
        headerElement: (
          <>
            <div className={styles['search-result-page-search-text']}>{text}</div>
            <div className={styles['search-result-page-search-count']}>
              {translate('search.searchResult.results')} ({`${totalCount()}`})
            </div>
          </>
        ),
        hasTabletScrolledHeaderBody: true,
        hasCart: true,
      }}
    >
      <div className={styles['search-results-container']} data-testid="search-results-container">
        <SearchResult
          searchedKeyword={router.query?.get('text') as string}
          searchMode={router.query?.get('searchMode') as string}
          goToShop={() => {
            router.push('shop');
          }}
        />
      </div>
    </Layout>
  );
};

export default SearchResultsPage;
